/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* Licensed under the MIT License. See LICENSE.md in the project root for license terms.
*--------------------------------------------------------------------------------------------*/

import { editor, Emitter, IDisposable, IEvent, Range, Uri, } from "monaco-editor/esm/vs/editor/editor.api";
import { IAnnotation, IAnnotationData, IAnnotationUpdateData } from "../../types/Annotation";
import { AnnotationService } from "./AnnotationService";

const createAnnotationDecorations = (textModel: editor.ITextModel, annotations: IAnnotation[]): { range: Range, options: editor.IModelDecorationOptions }[] => {
  const result: { range: Range; options: editor.IModelDecorationOptions; }[] = [];
  annotations.forEach((annotation) => {
    if (annotation.lineNumber <= textModel.getLineCount()) {
      const column = textModel.getLineFirstNonWhitespaceColumn(annotation.lineNumber);
      const range = textModel.validateRange(
        annotation.column ? new Range(annotation.lineNumber, annotation.column, annotation.endLineNumber, annotation.column)
          : new Range(annotation.lineNumber, column, annotation.endLineNumber, column + 1)
      );

      result.push({
        options: annotation.options,
        range
      });
    }
  });

  return result;
}

export interface IAnnotationDecoration {
  /** Decoration ID as related to the monaco-editor decoration */
  decorationId: string;
  /** Annotation data related to the decoration */
  annotation: IAnnotation;
  /** Range in the editor as related to the annotation */
  range: Range;
}

/** Manages the interface between the monaco-editor and the annotations component. */
export class AnnotationHandler {
  private _disposables: IDisposable[] = [];
  private _editor: editor.ICodeEditor;
  private _annotationService: AnnotationService;
  private _ignoreDecorationsChangedEvent: boolean = false;
  private _ignoreAnnotationsChangeEvent: boolean = false;
  private _annotationDecorations: IAnnotationDecoration[] = [];
  private readonly _onAnnotationClick: Emitter<IAnnotation> = new Emitter<IAnnotation>();

  public constructor(editor: editor.ICodeEditor) {
    this._annotationService = new AnnotationService();
    this._editor = editor;
    this.registerListeners();
  }

  private registerListeners(): void {
    this._disposables.push(this._editor.onMouseDown((event) => {
      // @ts-ignore
      const data = event.target.detail;
      const model = this._editor.getModel();
      if (!event.target.position || !model || event.target.type !== editor.MouseTargetType.GUTTER_GLYPH_MARGIN || data?.isAfterLines) {
        return;
      }

      const lineNumber = event.target.position.lineNumber;
      const uri = model.uri;

      const annotations = this._annotationService.getAnnotations({ uri, lineNumber, endLineNumber: lineNumber });

      if (annotations.length) {
        annotations.forEach((anno) => {
          this._onAnnotationClick.fire(anno);
        });
      }
    }));


    this._disposables.push(this._editor.onDidChangeModel(async () => {
      await this.setDecorations();
    }));

    this._disposables.push(this._annotationService.onDidChangeAnnotations(async () => {
      if (!this._ignoreAnnotationsChangeEvent) {
        await this.setDecorations();
      }
    }));

    this._disposables.push(this._editor.onDidChangeModelDecorations(() => this.onModelDecorationsChanged()));

    const prevOptions = this._editor.getRawOptions();
    this._editor.updateOptions({
      glyphMargin: true,
      lineNumbersMinChars: 3,
    })
    this._disposables.push({ dispose: () => { this._editor.updateOptions(prevOptions); } });

  }

  private async setDecorations(): Promise<void> {
    const activeCodeEditor = this._editor;
    const model = activeCodeEditor.getModel();
    if (!model) {
      return;
    }

    const annotations = this._annotationService.getAnnotations({ uri: model.uri });
    const desiredAnnotationDecorations = createAnnotationDecorations(model, annotations);

    try {
      this._ignoreDecorationsChangedEvent = true;
      const decorationIds = activeCodeEditor.deltaDecorations(this._annotationDecorations.map((anno) => anno.decorationId), desiredAnnotationDecorations);
      this._annotationDecorations = decorationIds.map((decorationId, index) => {
        const annotation = annotations[index];

        return {
          decorationId,
          annotation,
          range: desiredAnnotationDecorations[index].range,
        };
      });

    } finally {
      this._ignoreDecorationsChangedEvent = false;
    }
  }

  private async onModelDecorationsChanged(): Promise<void> {
    const model = this._editor.getModel();
    if (this._annotationDecorations.length === 0 || this._ignoreDecorationsChangedEvent || !model) {
      // I have no decorations
      return;
    }
    let somethingChanged = false;
    this._annotationDecorations.forEach((annotationDecoration) => {
      if (somethingChanged) {
        return;
      }
      const newAnnotationRange = model.getDecorationRange(annotationDecoration.decorationId);
      if (newAnnotationRange && (!annotationDecoration.range.equalsRange(newAnnotationRange))) {
        somethingChanged = true;
        annotationDecoration.range = newAnnotationRange;
      }
    });
    if (!somethingChanged) {
      // nothing to do, my decorations did not change.
      return;
    }

    const data = new Map<string, IAnnotationUpdateData>();
    for (let i = 0, len = this._annotationDecorations.length; i < len; i++) {
      const annotationDecoration = this._annotationDecorations[i];
      const decorationRange = model.getDecorationRange(annotationDecoration.decorationId);
      // check if the line got deleted.
      if (decorationRange) {
        // since we know it is collapsed, it cannot grow to multiple lines
        if (annotationDecoration.annotation) {
          data.set(annotationDecoration.annotation.id, {
            lineNumber: decorationRange.startLineNumber,
            column: annotationDecoration.annotation.column ? decorationRange.startColumn : undefined,
            endLineNumber: annotationDecoration.annotation.endLineNumber ? decorationRange.endLineNumber : undefined,
            endColumn: annotationDecoration.annotation.endColumn ? decorationRange.endColumn : undefined,
          });
        }
      }
    }

    try {
      this._ignoreAnnotationsChangeEvent = true;
      this._annotationService.updateAnnotations(data);
    } finally {
      this._ignoreAnnotationsChangeEvent = false;
    }
  }

  /** Add annotation data to the specified editor model. Returns the IDs of the specified annotation data. */
  public addAnnotations(uri: string, annotations: IAnnotationData[]): string[] {
    const modelUri = Uri.parse(uri)
    return this._annotationService.addAnnotations(modelUri, annotations).map((anno) => anno.id);
  }

  /** Removes the specified annotations by ID. */
  public removeAnnotations(ids: string[]): void {
    const annotations = this._annotationService.getAnnotations().filter((anno) => ids.includes(anno.id));
    this._annotationService.removeAnnotations(annotations);
  }

  /** Removes all annotations from the editor */
  public clearAnnotations(): void {
    const toRemoveAnnotations = this._annotationService.getAnnotations();
    this._annotationService.removeAnnotations(toRemoveAnnotations);
  }

  /** Provides a hook for when annotations are clicked on. */
  public get onAnnotationClick(): IEvent<IAnnotation> {
    return this._onAnnotationClick.event;
  }

  /** returns a list of IAnnotationDecoration */
  public getAnnotations() {
    return this._annotationDecorations;
  }

  /** Disposes of the annotations and the handlers */
  public dispose(): void {
    this.clearAnnotations();
    this._onAnnotationClick.dispose();
    this._disposables.forEach((disposable) => {
      disposable.dispose();
    })
  }
}